import { useRef } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { AlertTriangle, ArrowDown, ArrowUp, Skull } from 'lucide-react'
import clsx from 'clsx'
import type { ColumnSpec, DealRow } from './types'
import { NUMERIC_KINDS, cellText, columnWidth } from './format'

const ROW_HEIGHT = 34

/** Fixed-height virtualized grid.
 *
 *  Only the visible rows are ever in the DOM, and the server already bounded
 *  the payload to one page — so the row count the browser handles is constant
 *  regardless of how many deals matched. That is the whole answer to "must not
 *  fail on large result sets".
 */
export function ResultTable({
  rows,
  columns,
  selectedId,
  sortField,
  sortDir,
  sortableFields,
  loading,
  error,
  onSelect,
  onOpen,
  onSort,
}: {
  rows: DealRow[]
  columns: ColumnSpec[]
  selectedId: string | null
  sortField: string
  sortDir: 'asc' | 'desc'
  sortableFields: string[]
  loading: boolean
  error: string | null
  onSelect: (row: DealRow) => void
  /** Double-click: open the deal's file, the same one the drawer's
   *  "Open deal file" opens. */
  onOpen?: (row: DealRow) => void
  onSort: (field: string) => void
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
    // The virtualizer's scroll rect starts at 0×0 and is only corrected when
    // its ResizeObserver fires. On a first paint where that callback lands
    // after render — a background tab, a hidden pane, a slow mount — the
    // visible range computes as empty and the grid shows nothing while the
    // footer still reads "Showing 1–100 of 654". Seeding a plausible height
    // means the first frame renders real rows; the observer then corrects it.
    initialRect: { width: 0, height: 640 },
  })

  const template = columns.map((column) => columnWidth(column.key, column.kind)).join(' ')

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-amber-300/10 bg-[#0c0c0c]">
      {/* The grid scrolls as one unit horizontally so the header can never
          drift out of alignment with its body. */}
      <div className="min-h-0 flex-1 overflow-auto" ref={scrollRef}>
        <div style={{ minWidth: 'max-content' }}>
          <div
            role="row"
            className="sticky top-0 z-10 grid border-b border-amber-300/10 bg-[#121212]"
            style={{ gridTemplateColumns: template }}
          >
            {columns.map((column) => {
              const sortable = sortableFields.includes(column.key)
              const active = sortField === column.key
              return (
                <button
                  key={column.key}
                  type="button"
                  role="columnheader"
                  aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  disabled={!sortable}
                  onClick={() => sortable && onSort(column.key)}
                  title={sortable ? `Sort by ${column.label}` : `${column.label} (not sortable)`}
                  className={clsx(
                    'flex items-center gap-1 px-2.5 py-2 text-[9px] font-medium uppercase tracking-[0.12em] transition',
                    NUMERIC_KINDS.has(column.kind) && 'justify-end',
                    active ? 'text-amber-200' : 'text-stone-500',
                    sortable ? 'hover:text-amber-200' : 'cursor-default',
                  )}
                >
                  <span className="truncate">{column.label}</span>
                  {active &&
                    (sortDir === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />)}
                </button>
              )
            })}
          </div>

          {error ? (
            <div className="flex h-40 flex-col items-center justify-center gap-2 px-6 text-center">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <p className="text-[12px] text-stone-300">Search failed</p>
              <p className="max-w-md text-[11px] text-stone-500">{error}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center gap-1.5 text-center">
              <p className="text-[12px] text-stone-400">
                {loading ? 'Searching…' : 'No deals match these filters.'}
              </p>
              {!loading && (
                <p className="text-[11px] text-stone-600">
                  Dead deals are excluded unless you include them — check the status filter.
                </p>
              )}
            </div>
          ) : (
            <div
              className="relative"
              style={{ height: `${virtualizer.getTotalSize()}px` }}
              aria-busy={loading}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index]
                const dead = row.deal_status === 'dead'
                const selected = selectedId === row.id
                return (
                  <div
                    key={row.id}
                    role="row"
                    tabIndex={0}
                    // A double-click delivers two clicks first. The second one
                    // used to toggle the drawer shut again, so the gesture
                    // opened and closed it and looked like nothing happened.
                    onClick={(event) => {
                      if (event.detail > 1) return
                      onSelect(row)
                    }}
                    onDoubleClick={onOpen ? () => onOpen(row) : undefined}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onSelect(row)
                      }
                    }}
                    className={clsx(
                      'absolute left-0 grid w-full cursor-pointer border-b border-white/[0.03] text-[11px] transition',
                      'focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-amber-300/60',
                      selected
                        ? 'bg-amber-300/[0.09] text-stone-100'
                        : virtualRow.index % 2 === 1
                          ? 'bg-white/[0.012] hover:bg-white/[0.035]'
                          : 'hover:bg-white/[0.035]',
                      dead && !selected && 'text-stone-500',
                    )}
                    style={{
                      gridTemplateColumns: template,
                      height: `${ROW_HEIGHT}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {columns.map((column) => (
                      <div
                        key={column.key}
                        role="cell"
                        className={clsx(
                          'flex items-center gap-1 truncate px-2.5',
                          NUMERIC_KINDS.has(column.kind) && 'justify-end font-mono',
                        )}
                        title={cellText(row, column.key, column.kind)}
                      >
                        {column.key === 'deal_status' ? (
                          <StatusPill status={row.deal_status} />
                        ) : column.key === 'completeness' ? (
                          <Completeness value={row.completeness} missing={row.missing_fields} />
                        ) : (
                          <span className="truncate">
                            {cellText(row, column.key, column.kind)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** Status carries an icon as well as a colour — colour alone would be the only
 *  cue that a deal is dead. */
function StatusPill({ status }: { status: string }) {
  const dead = status === 'dead'
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider',
        dead
          ? 'bg-red-500/10 text-red-300 ring-1 ring-inset ring-red-500/25'
          : 'bg-emerald-500/10 text-emerald-300 ring-1 ring-inset ring-emerald-500/20',
      )}
    >
      {dead && <Skull size={8} />}
      {dead ? 'Dead' : 'Live'}
    </span>
  )
}

/** `value` is the fraction the server sends for every 'percent' column
 *  (0.187 for 18.7%). It used to arrive as points, which the column's own
 *  formatter then multiplied by 100 into "1870.0%" (R2). */
export function Completeness({ value, missing }: { value: number; missing: number }) {
  const points = value * 100
  return (
    <span
      className="inline-flex w-full items-center justify-end gap-1.5"
      title={
        missing === 0 ? 'All catalog fields hold a value' : `${missing} catalog field(s) missing`
      }
    >
      <span className="h-1 w-8 overflow-hidden rounded-full bg-white/10">
        <span
          className={clsx(
            'block h-full',
            points >= 90 ? 'bg-emerald-400/70' : points >= 60 ? 'bg-amber-400/70' : 'bg-red-400/70',
          )}
          style={{ width: `${Number(points.toFixed(1))}%` }}
        />
      </span>
      <span className="font-mono text-[10px]">{points.toFixed(0)}%</span>
    </span>
  )
}
